// Boot the in-world game: renderer, world, net, input, audio, HUD. Runs
// after the flow in main.ts has a signed-in user with a character. The
// scene (street or club) follows world.roomId — walking through a door
// swaps the set dressing while everything else keeps running.

import {
  type AreaPlan,
  aabbCenter,
  asWorldId,
  boothAudioPosition,
  CLAIM_READ_RANGE_M,
  DEFAULT_SERVER_PORT,
  FIRE_COOLDOWN_MS,
  FLYER_REACH_M,
  isPlaceableKind,
  JOB_BOARD,
  JOB_BOARD_RANGE_M,
  MARKET_BOARD,
  MARKET_BOARD_RANGE_M,
  type NightBill,
  PLACEABLE_SPECS,
  PLANS,
  planFor,
  resolvePlan,
  SHIP_HOVER,
  SHIP_LANDED_MAX_SPEED,
  SHIP_LANDED_TOLERANCE_M,
  SHIP_MAX_SPEED,
  SHIP_MOUNT_RANGE,
  SKIFF_MOUNT_RANGE,
  SPACE_DESCENT_Y,
  SPACE_SPEED_SCALE,
  STRIKE_REACH_M,
  SURFACE_CEILING_M,
  streamPathForClub,
} from "@bebop/shared";
import * as THREE from "three";
import { PortAmbience } from "./audio/ambience";
import { Broadcaster } from "./audio/broadcast";
import { ClubAudio } from "./audio/club-audio";
import { WildsAudio } from "./audio/wilds-audio";
import { InputController } from "./core/input";
import { GameLoop } from "./core/loop";
import { NetClient } from "./core/net";
import { fetchNights } from "./core/nights";
import { World } from "./core/world";
import { type AreaScene, createAreaScene } from "./render/area";
import type { LightPhase } from "./render/atmosphere";
import { AvatarLayer } from "./render/avatars";
import { ThirdPersonCamera } from "./render/camera";
import { CreatureLayer } from "./render/creatures";
import { applyFreecam, parseFreecam } from "./render/freecam";
import { HomeInteriorLayer, HomesteadLayer } from "./render/homesteads";
import {
  clampShadowMaps,
  detectTier,
  FrameGauge,
  TIERS,
  TierGovernor,
  warmScene,
} from "./render/perf";
import { DEEP_FOCUS, DEFAULT_POST, PostStack } from "./render/post";
import { ShipLayer } from "./render/ships";
import { SkiffLayer } from "./render/skiffs";
import { TracerLayer } from "./render/tracers";
import { WardenLayer } from "./render/wardens";
import { AetherWipe } from "./render/wipe";
import { BenchPanel } from "./ui/bench";
import { BoothControls } from "./ui/broadcast";
import { ChatOverlay } from "./ui/chat";
import { FlightInstrument } from "./ui/flight";
import { SurfaceHud } from "./ui/hud";
import { InventoryPanel } from "./ui/inventory";
import { JobsPanel } from "./ui/jobs";
import { LedgerPanel } from "./ui/ledger";
import { MarketPanel } from "./ui/market";
import { MusicPill } from "./ui/music";
import { NightsPanel } from "./ui/nights";
import { WorldPrompt } from "./ui/prompt";
import { PocketRadio } from "./ui/radio";
import { SurveyPanel } from "./ui/survey";
import { TradePrompt } from "./ui/trade";

/** How often the street re-reads the bill. Cheap: one small GET. */
const NIGHTS_REFRESH_MS = 60_000;

/** Where a coil shot converges on the camera ray, metres — roughly the
 *  middle of a warden fight's engagement band. */
const AIM_CONVERGE_M = 320;

/** Standing close enough to a settlement board to work it. */
function nearBoard(
  pose: { x: number; z: number },
  board: { minX: number; maxX: number; minZ: number; maxZ: number },
  rangeM: number,
): boolean {
  const centre = aabbCenter(board);
  return Math.hypot(pose.x - centre.x, pose.z - centre.z) <= rangeM;
}

/**
 * The boarding offer for a nearby hull, composed from replicated words.
 * The label tells you whose it is; "dry" is read at the machine, before
 * boarding, so running out is never a surprise (trades.md §13).
 */
function boardPrompt(world: World, skiffId: string): string | null {
  const skiff = world.skiffs.get(skiffId);
  if (!skiff) return "E — board the skiff";
  const mine = skiff.ownerName !== "" && skiff.ownerName === world.self?.name;
  const label = skiff.town
    ? "the town skiff"
    : mine
      ? "your skiff"
      : skiff.ownerName
        ? `${skiff.ownerName}'s skiff`
        : "the derelict skiff";
  if (skiff.locked && !mine && !skiff.town) return `${label} — locked`;
  // A driven hull offers its second seat, and the placard says where it
  // is bound. Words only; the client draws no bearing toward it.
  if (skiff.riderId) return rideAlongPrompt(world, skiff.riderId, skiff.plotted, label);
  const dry = skiff.chargeBand === "dry" ? " (dry)" : "";
  return `E — board ${label}${dry}`;
}

/** The boarding offer for a nearby ship, same idiom as the skiffs'. */
function shipBoardPrompt(world: World, shipId: string): string | null {
  const ship = world.ships.get(shipId);
  if (!ship) return "E — board the ship";
  const mine = ship.ownerName !== "" && ship.ownerName === world.self?.name;
  const label = mine ? "your ship" : ship.ownerName ? `${ship.ownerName}'s ship` : "the ship";
  if (ship.locked && !mine) return `${label} — locked`;
  if (ship.riderId) return rideAlongPrompt(world, ship.riderId, ship.plotted, label);
  const dry = ship.chargeBand === "dry" ? " (dry)" : "";
  return `E — board ${label}${dry}`;
}

/** "E — ride along with Vex (bound for the Wreck)". */
function rideAlongPrompt(world: World, riderId: string, plotted: string, label: string): string {
  const driver = world.remotes.get(riderId)?.name ?? "the pilot";
  const bound = plotted ? ` (bound for ${plotted})` : "";
  return `E — ride along with ${driver}${bound} — ${label}`;
}

export function startGame(app: HTMLElement): void {
  // Dev-only capture mode (tools/capture): a URL-driven freecam detaches
  // the render camera from the avatar so a headless browser can frame
  // named vistas. Never parsed in production builds.
  const freecam = import.meta.env.DEV ? parseFreecam(location.search) : undefined;
  const freecamPose = freecam ? { x: freecam.x, z: freecam.z, yaw: freecam.yaw } : undefined;
  // Outdoor lighting phase (wave 9): the surface defaults to the fixed
  // art-directed day sun; ?phase=night (dev-only) keeps the pre-wave-9
  // moonlit regime available for the night-city work and its captures.
  const phase: LightPhase =
    import.meta.env.DEV && new URLSearchParams(location.search).get("phase") === "night"
      ? "night"
      : "day";

  // --- core (never imports three) -------------------------------------------
  const world = new World();
  const serverUrl =
    (import.meta.env.VITE_SERVER_URL as string | undefined) ??
    `ws://${location.hostname}:${DEFAULT_SERVER_PORT}`;
  const net = new NetClient(world, serverUrl);
  const input = new InputController();
  const loop = new GameLoop(world, input, net);

  // If the session dies mid-play, restart the flow from the landing screen.
  net.onSignedOut = () => location.reload();

  // --- render ----------------------------------------------------------------
  // preserveDrawingBuffer keeps the frame readable after compositing so
  // screenshots (devtools, capture tooling) see the scene instead of black.
  // Costs a buffer copy per frame — trivial at gray-box draw-call counts.
  //
  // `?post=0` (dev only) is the control group for the perf guard: it puts
  // the client back on the pre-post-stack path — multisampled canvas, no
  // passes — so "what did this layer cost on this vista" can be measured
  // in one session against the build it replaced, instead of against a
  // number someone wrote down on a different day on a different machine.
  const postOff = import.meta.env.DEV && new URLSearchParams(location.search).get("post") === "0";
  // `antialias: false` is not a downgrade — multisampling moved. The scene
  // is drawn into the post stack's own multisampled buffer (render/post.ts,
  // 4× at the top tier), and the canvas now receives exactly one
  // full-screen triangle. Leaving the default framebuffer multisampled too
  // would cost a second 1080p×4 resolve every frame to antialias a
  // triangle that covers the screen.
  const renderer = new THREE.WebGLRenderer({ antialias: postOff, preserveDrawingBuffer: true });
  const tier = detectTier(renderer);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, TIERS[tier].maxPixelRatio));
  renderer.setSize(window.innerWidth, window.innerHeight);
  // Draw-call and triangle counts are read once per FRAME by the perf
  // guard, and a frame is many render() calls (scene, then every post
  // pass). three resets the counters inside each one unless told not to.
  renderer.info.autoReset = false;
  // Unified filmic grade (gold-standard §e): ACES everywhere, with the
  // per-biome "LUT" expressed by the atmosphere rig's blended palettes.
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  // Sun shadows on the surface (day phase); interiors ignore the map.
  // Wave 10 round 2 — PCF soft, NOT VSM. The w10-r1 critics measured that
  // nothing smaller than a building cast a shadow: boulders, the 30 m
  // crashed hull, crates, reeds and the avatar all rendered with only a
  // radial AO ring. VSM is why. Its variance estimate reports "lit" for
  // any occluder whose depth spread is small relative to the blur — i.e.
  // exactly the near-field props whose contact shadows the §e shadow law
  // (t035's ladder) is built on — and the light-bleed reduction then eats
  // what survives. PCF soft resolves a 0.4 m pebble at this texel density
  // and hardens naturally at contact.
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  app.append(renderer.domElement);

  // The post stack owns the frame from here: the scene renders into its
  // own multisampled buffer — graded exactly as it was when it went
  // straight to the canvas — and comes back occluded and bloomed.
  const post = new PostStack(renderer, tier);
  post.bypass = postOff;
  // Deep-focus defocus rides the cinematic rig only — the freecam the
  // capture harness drives. Traversal is deep focus by the audit's own
  // reading of the reference; `?dof=0` takes it off a capture too.
  const dofOff = import.meta.env.DEV && new URLSearchParams(location.search).get("dof") === "0";
  post.setDof(freecam && !dofOff ? DEEP_FOCUS : undefined);
  const gauge = new FrameGauge();
  const governor = new TierGovernor(tier);
  const drawingBuffer = new THREE.Vector2();

  const avatars = new AvatarLayer();
  const skiffs = new SkiffLayer();
  const ships = new ShipLayer();
  const wardens = new WardenLayer();
  const tracers = new TracerLayer();
  const homesteads = new HomesteadLayer();
  const homeInterior = new HomeInteriorLayer();
  const creatures = new CreatureLayer();
  const camera = new ThirdPersonCamera(window.innerWidth / window.innerHeight);
  world.onBolt = (bolt) => tracers.spawn(bolt);

  // The aether wipe: the branded mask over every flight-room handoff. It
  // sits above the 3D canvas and below the persistent UI layer, so chat
  // and panels ride straight through the swap — UI continuity is the
  // "one world" storytelling.
  const wipe = new AetherWipe(app);
  net.onTransition = (from, to) => {
    const fromKind = from && PLANS[from] ? planFor(from).kind : undefined;
    const toKind = PLANS[to] ? planFor(to).kind : undefined;
    if (fromKind === "space" || toKind === "space") wipe.play();
  };

  window.addEventListener("resize", () => {
    renderer.setSize(window.innerWidth, window.innerHeight);
    camera.setAspect(window.innerWidth / window.innerHeight);
    renderer.getDrawingBufferSize(drawingBuffer);
    post.setSize(drawingBuffer.x, drawingBuffer.y);
  });

  // --- audio -------------------------------------------------------------------
  const clubAudio = new ClubAudio("", { x: 0, y: 1.6, z: 0 });
  // Distant horns off the port — Meridian's surface only.
  const ambience = new PortAmbience();
  // Any first gesture unlocks the audio graph (browser autoplay policy).
  const unlockOnGesture = () => {
    clubAudio.unlock();
    ambience.unlock();
  };
  window.addEventListener("pointerdown", unlockOnGesture, { once: true });
  window.addEventListener("keydown", unlockOnGesture, { once: true });

  // --- ui ----------------------------------------------------------------------
  const chat = new ChatOverlay(
    app,
    (text) => net.sendChat(text),
    (focused) => {
      input.uiFocused = focused;
    },
  );
  // Any focused text field anywhere — the chat box, a board panel's lot
  // form — must park the avatar and keep its keys (trades.md item 5). The
  // chat callback above stays for its explicit open/close moments; this
  // catches every panel input without each panel having to remember to.
  const refreshUiFocus = () => {
    const el = document.activeElement;
    const typing =
      !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT");
    if (typing) input.uiFocused = true;
    else if (!chat.isFocused()) input.uiFocused = false;
  };
  document.addEventListener("focusin", refreshUiFocus);
  document.addEventListener("focusout", () => setTimeout(refreshUiFocus, 0));
  world.onChat = (entry) => chat.addEntry(entry);
  const musicPill = new MusicPill(app, clubAudio);
  const broadcaster = new Broadcaster();
  const booth = new BoothControls(app, broadcaster);
  const hud = new SurfaceHud(app);
  const flight = new FlightInstrument(app);
  const prompt = new WorldPrompt(app);
  const surveyPanel = new SurveyPanel(app, {
    survey: (id) => net.sendSurvey(id),
    sample: (id) => net.sendSample(id),
    seal: () => net.sendSealReading(),
  });
  const inventoryPanel = new InventoryPanel(app, {
    peopleWithinReach: () => world.peopleWithinReach(),
    offerUnits: (toSessionId, spawnId, units) =>
      net.sendTradeOffer({ toSessionId, kind: "units", spawnId, units }),
    offerReading: (toSessionId, readingId) =>
      net.sendTradeOffer({ toSessionId, kind: "reading", readingId }),
    offerRouteReading: (toSessionId, readingId) =>
      net.sendTradeOffer({ toSessionId, kind: "route_reading", readingId }),
  });
  const tradePrompt = new TradePrompt(app, (offerId, accept) =>
    net.sendTradeRespond(offerId, accept),
  );
  const ledgerPanel = new LedgerPanel(app, {
    peopleWithinReach: () => world.peopleWithinReach(),
    offerArtifact: (toSessionId, artifactId) =>
      net.sendTradeOffer({ toSessionId, kind: "artifact", artifactId }),
    useArtifact: (artifactId, verb) => net.sendUseArtifact(artifactId, verb),
  });
  const nightsPanel = new NightsPanel(app);
  const benchPanel = new BenchPanel(app);
  const marketPanel = new MarketPanel(app, {
    post: (spawnId, giveUnits, wantClassId, wantUnits) =>
      net.sendMarketPost({ spawnId, giveUnits, wantClassId, wantUnits }),
    fill: (offerId, spawnId) => net.sendMarketFill({ offerId, spawnId }),
    withdraw: (offerId) => net.sendMarketWithdraw(offerId),
  });
  world.onMarketResult = (m) => {
    prompt.flash(m.message);
    marketPanel.refreshIfOpen();
  };
  const jobsPanel = new JobsPanel(app, {
    postSupply: (wantClassId, wantUnits, rewardSpawnId, rewardUnits) =>
      net.sendJobPost({ kind: "supply", wantClassId, wantUnits, rewardSpawnId, rewardUnits }),
    postCharter: (destinationLandmarkId, rewardSpawnId, rewardUnits) =>
      net.sendJobPost({ kind: "charter", destinationLandmarkId, rewardSpawnId, rewardUnits }),
    accept: (jobId) => net.sendJobAccept(jobId),
    deliver: (jobId, spawnId) => net.sendJobDeliver(jobId, spawnId),
    cancel: (jobId) => net.sendJobCancel(jobId),
  });
  world.onJobResult = (m) => {
    prompt.flash(m.message);
    jobsPanel.refreshIfOpen();
  };

  // One bill, two readers: the board on the street and the panel on N.
  // Refreshed on a slow tick and immediately after anybody prints, so a
  // flyer goes up on the wall while its maker is still standing there.
  let nights: NightBill[] = [];
  async function refreshNights(): Promise<void> {
    try {
      nights = await fetchNights();
      area?.setNights(nights);
    } catch {
      // A wall that cannot reach the server keeps yesterday's bill.
    }
  }
  benchPanel.onPrinted = () => {
    void refreshNights();
    void nightsPanel.refreshIfOpen();
  };
  const radio = new PocketRadio(app, clubAudio);
  // The wilds speaking: one fading line, one quiet cue. Words and sound,
  // never a marker.
  const wildsAudio = new WildsAudio();
  world.onThreat = (m) => {
    hud.showThreat(m.text);
    wildsAudio.cue(m.kind);
  };
  world.onSurveyResult = (m) => surveyPanel.showSurvey(m);
  world.onSampleResult = (m) => surveyPanel.showSample(m);
  world.onTradeRequest = (m) => tradePrompt.show(m);
  world.onTradeResult = (m) => tradePrompt.showResult(m);
  world.onFlyerTaken = (m) => {
    prompt.flash(m.message);
    // It is on your shelf now; if the Ledger is open, show it.
    if (m.ok) void ledgerPanel.refreshIfOpen();
  };
  world.onArtifactUsed = (m) => {
    prompt.flash(m.message);
    if (m.ok) void ledgerPanel.refreshIfOpen();
  };

  // Panel toggles (movement keys stay in core/input).
  window.addEventListener("keydown", (e) => {
    if (input.uiFocused || e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
    // The aetherometer answers only where the ground yields — the wilds.
    if (e.code === "KeyR" && areaPlan?.wilds) surveyPanel.toggle();
    if (e.code === "KeyI") inventoryPanel.toggle();
    if (e.code === "KeyL") ledgerPanel.toggle();
    if (e.code === "KeyN") nightsPanel.toggle();
    // The bench is a place, not a menu: it only opens where it stands.
    if (e.code === "KeyB" && areaPlan?.kind === "workshop") benchPanel.toggle();
    // The field guide is a page, not a panel — it should survive being read
    // on a phone while somebody else is DJing. Link the .html explicitly: a
    // bare /guide serves the game, because the labs Caddy falls back to
    // index.html for extensionless paths and never tries {path}/index.html
    // either (which is why shipping it as a directory made it worse).
    if (e.code === "Slash" || e.code === "KeyH") window.open("/guide.html", "_blank", "noopener");
  });

  input.attach(renderer.domElement);

  // --- area scene, following world.roomId --------------------------------------
  let area: AreaScene | undefined;
  let areaPlan: AreaPlan | undefined;
  /** The room actually joined (differs from areaPlan under ?scene=). */
  let joinedRoomId: string | undefined;
  /** Frames rendered since the current area was entered (capture readiness). */
  let framesInArea = 0;
  let lastHomeLabel = "";

  function enterArea(roomId: string): void {
    framesInArea = 0;
    joinedRoomId = roomId;
    if (area) {
      area.worldAnchor.remove(avatars.group);
      area.worldAnchor.remove(skiffs.group);
      area.worldAnchor.remove(ships.group);
      area.worldAnchor.remove(wardens.group);
      area.worldAnchor.remove(tracers.group);
      area.worldAnchor.remove(homesteads.group);
      area.worldAnchor.remove(homeInterior.group);
      area.worldAnchor.remove(creatures.group);
      area.dispose();
    }
    // The capture freecam may render another plan's set over this room's
    // session (?scene= — see freecam.ts for why the Black needs it).
    areaPlan = resolvePlan(freecam?.scene ?? roomId);
    area = createAreaScene(areaPlan, phase);
    // A scene may ship its authored tone-mapping operator (the Understreet
    // is graded linear); everything else gets the house ACES grade. The
    // operator now runs in the post stack's composite instead of in every
    // material — same curve, applied once, after bloom and occlusion.
    renderer.toneMapping = area.toneMapping ?? THREE.ACESFilmicToneMapping;
    // …and a set may ask for its own post treatment (the night district
    // was authored around baked glow and wants far less of ours).
    post.setProfile(area.post ?? DEFAULT_POST);
    area.setNights(nights);
    area.worldAnchor.add(avatars.group);
    area.worldAnchor.add(skiffs.group);
    area.worldAnchor.add(ships.group);
    area.worldAnchor.add(wardens.group);
    area.worldAnchor.add(tracers.group);
    area.worldAnchor.add(homesteads.group);
    area.worldAnchor.add(homeInterior.group);
    area.worldAnchor.add(creatures.group);
    avatars.groundY = area.groundY;
    skiffs.groundY = area.groundY;
    creatures.groundY = area.groundY;
    camera.setFar(area.cameraFar);
    camera.setFov(area.fov ?? 70);
    hud.setVisible(areaPlan.kind === "surface");
    // The aetherometer is a wilds instrument; it doesn't follow indoors,
    // and Meridian's paving gives it nothing to read.
    if (!areaPlan.wilds) surveyPanel.close();
    // Walk out of the workshop and the bench closes behind you.
    if (areaPlan.kind !== "workshop") benchPanel.close();
    // The boards stand on the street; indoors they close behind you.
    if (areaPlan.kind !== "surface") {
      marketPanel.close();
      jobsPanel.close();
    }
    // The radio is for out on the ground — indoors you can hear the room.
    // A district is a street too; the Understreet gets the dial.
    radio.setVisible(areaPlan.kind === "surface" || areaPlan.kind === "district");
    // The port's horns carry only on Meridian's open crown.
    ambience.setActive(areaPlan.kind === "surface" && areaPlan.world === "meridian");

    if (areaPlan.mount) {
      clubAudio.setStream(streamPathForClub(areaPlan.mount), boothAudioPosition(areaPlan));
    } else {
      clubAudio.setLive(false);
    }
    lastHomeLabel = "";
    chat.setAreaLabel(areaPlan.label);

    // The shadow map is sized before it is allocated, and only ever down.
    clampShadowMaps(area.scene, post.currentTier());
    // Warm the set BEFORE its first frame. Lazy program linking and lazy
    // texture upload are the classic hidden stalls: a set that reports 90
    // fps median can be hitching to 12 whenever a material or a 2048²
    // forged canvas reaches the GPU for the first time, and a scene swap
    // presents dozens of them at once. Paying for them here puts the whole
    // cost inside the frame the wipe is already covering — measured on the
    // day-city vistas as max frame time 2316 ms → 10 ms, stalls 2 → 0.
    warmScene(renderer, area.scene, camera.camera);
    post.warm();
    gauge.reset();
    governor.reset();
  }

  // --- wire and run ------------------------------------------------------------
  const listenerForward = new THREE.Vector3();
  const aimForward = new THREE.Vector3();
  let lastFireSentAt = 0;
  /** Last brace state told to the server (edge-triggered, held stance). */
  let braceSent = false;
  let lastFrame = performance.now();
  renderer.setAnimationLoop(() => {
    const now = performance.now();
    const rawMs = now - lastFrame;
    const dt = Math.min(rawMs / 1000, 0.1);
    lastFrame = now;
    // The perf guard sees raw frame times, never an average: a smoothed
    // fps is exactly the statistic a periodic compile stall vanishes into
    // (render/perf.ts). The governor demotes the tier when p95 misses.
    gauge.push(rawMs);
    const demoted = governor.sample(rawMs);
    if (demoted) post.setTier(demoted);

    // The scene swap waits for the wipe's full cover (pre-warm: the room
    // join already ran behind the ramp), then happens invisibly. If no
    // wipe is running — door portals, reconnects — it swaps immediately.
    if (world.roomId && world.roomId !== joinedRoomId && !wipe.holdSwap) {
      enterArea(world.roomId);
    }
    if (!area || !areaPlan) return;

    // E is "use the thing in front of you": a ship or skiff out on the
    // ground, the pile of flyers by the door in a club. The server
    // validates range and state — this only decides what the player was
    // reaching for. In the Black there is nothing to reach for.
    if (input.consumeInteract() && world.self) {
      if (areaPlan.kind === "surface") {
        if (world.self.mounted) {
          net.sendDismount();
        } else {
          const nearShip = world.nearestShip(world.self.pose, SHIP_MOUNT_RANGE);
          const nearSkiff = world.nearestSkiff(world.self.pose, SKIFF_MOUNT_RANGE);
          if (nearShip) {
            net.sendMount(nearShip);
          } else if (nearSkiff) {
            net.sendMount(nearSkiff);
          } else if (nearBoard(world.self.pose, MARKET_BOARD, MARKET_BOARD_RANGE_M)) {
            marketPanel.toggle();
          } else if (nearBoard(world.self.pose, JOB_BOARD, JOB_BOARD_RANGE_M)) {
            jobsPanel.toggle();
          } else {
            // Nothing to ride, no board at hand: maybe a claim to read.
            const stake = world.nearestHomestead(world.self.pose, CLAIM_READ_RANGE_M);
            if (stake) net.sendHomesteadRead(stake.id);
          }
        }
      } else if (areaPlan.kind === "home") {
        // The body is the cursor: pick up what's at hand, or set down
        // what you carry where you stand. The server rules on both.
        const carried = world.carriedPlaceable();
        if (carried) {
          net.sendFurnishHandle();
        } else {
          const near = world.nearestPlaceable(world.self.pose, 2.2);
          if (near) net.sendFurnishHandle(near);
        }
      } else if (areaPlan.flyerStack) {
        net.sendTakeFlyer();
      }
    }

    // The routing family: press a manifold's key to favor it; press the
    // one you already favor to vent back to even.
    const routeKey = input.consumeRoute();
    if (routeKey && world.selfShip) {
      const flown = world.ships.get(world.selfShip.id);
      net.sendRoute(flown?.focus === routeKey ? "even" : routeKey);
    }

    // The defensive verbs: on foot, on the surface. A strike stays quiet
    // when nothing is plainly in reach (the server re-validates and its
    // answer wins); brace reports the key's edges so the stance is held,
    // not tapped.
    const combatKey = input.consumeCombat();
    if (areaPlan.kind === "surface" && world.self && !world.self.mounted) {
      if (combatKey === "strike") {
        if (world.creatureInReach(world.self.pose, STRIKE_REACH_M + 1.2)) {
          net.sendCombat("strike");
        }
      } else if (combatKey) {
        net.sendCombat(combatKey);
      }
      if (input.bracing !== braceSent) {
        braceSent = input.bracing;
        net.sendCombat("brace", braceSent);
      }
    }

    loop.update(now);
    avatars.sync(world, dt);
    skiffs.sync(world, dt);
    ships.sync(world, dt);
    wardens.sync(world, dt);
    tracers.tick(dt);
    homesteads.sync(world);
    homeInterior.sync(world, world.self?.pose);

    // The coil gun: hold F, shots at the server's cadence, aimed at the
    // point under the reticle (the gun rides a gimbal). The camera sits
    // metres behind and beside the hull, so the shot must CONVERGE on
    // the camera ray — firing raw camera-forward from the ship's
    // position misses by the parallax at fighting range. The Black only.
    if (
      areaPlan.kind === "space" &&
      world.selfShip &&
      input.firing &&
      now - lastFireSentAt >= FIRE_COOLDOWN_MS
    ) {
      lastFireSentAt = now;
      camera.camera.getWorldDirection(aimForward);
      const shipPose = world.selfShip.pose;
      const converge = area.origin();
      const ax = camera.camera.position.x + converge.x + aimForward.x * AIM_CONVERGE_M;
      const ay = camera.camera.position.y + aimForward.y * AIM_CONVERGE_M;
      const az = camera.camera.position.z + converge.z + aimForward.z * AIM_CONVERGE_M;
      net.sendFire(ax - shipPose.x, ay - (shipPose.y + 0.4), az - shipPose.z);
    }
    creatures.sync(world, dt);

    // Area first (it may rebase the floating origin), then the camera in
    // render space against the fresh origin.
    const pose = world.self?.pose;
    area.setLive(world.music.live);
    // The freecam substitutes its own pose so the surface streams terrain
    // (and rebases the floating origin) around the shot, not the avatar.
    area.tick(dt, clubAudio.level(), freecamPose ?? pose);
    const origin = area.origin();
    const mounted = (world.self?.mounted ?? "") !== "";
    const flying = world.selfShip !== undefined;
    // Flying widens the camera's pitch range below the horizon (the coil
    // gun aims along the camera); on foot the floor comes back.
    input.flightCamera = flying;
    if (!flying && input.orbit.pitch < 0.08) input.orbit.pitch = 0.08;
    if (freecam) {
      applyFreecam(camera.camera, freecam, origin, area.groundY);
    } else if (pose) {
      // Mounted, the camera pulls back and drops low — vehicle framing.
      // At a ship's controls it pulls back further still, and the rig's
      // base elevation is the ship's own altitude, not the ground.
      const orbit = flying
        ? {
            yaw: input.orbit.yaw,
            pitch: input.orbit.pitch,
            distance: Math.min(input.orbit.distance + 9, 20),
          }
        : mounted
          ? {
              yaw: input.orbit.yaw,
              pitch: Math.max(0.14, input.orbit.pitch * 0.7),
              distance: Math.min(input.orbit.distance + 3.5, 12.5),
            }
          : input.orbit;
      camera.update(
        { x: pose.x - origin.x, z: pose.z - origin.z, yaw: pose.yaw },
        orbit,
        world.selfShip ? world.selfShip.pose.y : area.groundY(pose.x, pose.z),
      );
    }

    // Flight instrument: mode-gated — it exists only at a ship's
    // controls, in either sky. Analog language only; no digits. The
    // reticle rides only where the gun is live.
    flight.setVisible(flying);
    flight.setReticle(
      flying && areaPlan.kind === "space" && !world.ships.get(world.selfShip?.id ?? "")?.adrift,
    );
    if (flying && world.selfShip && pose) {
      const flown = world.ships.get(world.selfShip.id);
      const inSpace = areaPlan.kind === "space";
      const ground = inSpace ? SPACE_DESCENT_Y : area.groundY(pose.x, pose.z) + SHIP_HOVER;
      // The lift arc spans this sky's meaningful band: surface hover →
      // ascent ceiling, or descent floor → the arrival shelf's height.
      const ceiling = inSpace ? 800 : SURFACE_CEILING_M;
      const above = world.selfShip.pose.y - ground;
      const top = SHIP_MAX_SPEED * (inSpace ? SPACE_SPEED_SCALE : 1) * 2;
      flight.update(
        {
          focus: flown?.focus ?? "even",
          boost: flown?.boost ?? 0,
          coil: flown?.coil ?? 1,
          veil: flown?.veil ?? 1,
          boosting: flown?.boosting ?? false,
          speedFrac: Math.min(1, Math.abs(world.selfShip.pose.speed) / top),
          liftFrac: Math.max(0, Math.min(1, above / ceiling)),
          wearBand: flown?.wearBand ?? "true",
          chargeBand: flown?.chargeBand ?? "full",
          inSpace,
          strain: flown?.strain ?? 0,
          adrift: flown?.adrift ?? false,
        },
        dt,
      );
    }

    if (areaPlan.kind === "space") {
      // The Black: no ground HUD, no prompts — the instrument is the UI.
      hud.setVisible(false);
      prompt.set(null);
    } else if (areaPlan.kind === "surface") {
      hud.setVisible(!flying);
      hud.update(
        asWorldId(areaPlan.world),
        pose,
        input.orbit.yaw,
        input.walking,
        world.selfSkiff ? Math.abs(world.selfSkiff.pose.speed) : undefined,
      );
      hud.setCarriage(world.self?.carriage ?? "");
      // The two-word chip while riding: wear band · charge band. Words
      // only — the integers behind them never reach this client at all.
      const ridden = world.selfSkiff ? world.skiffs.get(world.selfSkiff.id) : undefined;
      hud.setRide(ridden?.wearBand, ridden?.chargeBand);
      if (flying && world.selfShip && pose) {
        // Dismount is offered only when she is set down; mid-air the
        // offer would be a lie the server refuses.
        const ground = area.groundY(pose.x, pose.z) + SHIP_HOVER;
        const landed =
          world.selfShip.pose.y <= ground + SHIP_LANDED_TOLERANCE_M &&
          Math.abs(world.selfShip.pose.speed) <= SHIP_LANDED_MAX_SPEED;
        prompt.set(landed ? "E — dismount" : null);
      } else {
        const nearShip = !mounted && pose ? world.nearestShip(pose, SHIP_MOUNT_RANGE) : undefined;
        const nearSkiff =
          !mounted && !nearShip && pose ? world.nearestSkiff(pose, SKIFF_MOUNT_RANGE) : undefined;
        const atMarket = !mounted && pose && nearBoard(pose, MARKET_BOARD, MARKET_BOARD_RANGE_M);
        const atJobs = !mounted && pose && nearBoard(pose, JOB_BOARD, JOB_BOARD_RANGE_M);
        const stake =
          !mounted && !nearShip && !nearSkiff && !atMarket && !atJobs && pose
            ? world.nearestHomestead(pose, CLAIM_READ_RANGE_M)
            : undefined;
        prompt.set(
          mounted
            ? "E — dismount"
            : nearShip
              ? shipBoardPrompt(world, nearShip)
              : nearSkiff
                ? boardPrompt(world, nearSkiff)
                : atMarket
                  ? "E — the offer board"
                  : atJobs
                    ? "E — the job board"
                    : stake
                      ? `E — read the claim at ${stake.label}`
                      : null,
        );
        // Walk away and the boards close behind you, like the bench.
        if (!atMarket && marketPanel.isOpen()) marketPanel.close();
        if (!atJobs && jobsPanel.isOpen()) jobsPanel.close();
      }
    } else if (areaPlan.kind === "home" && pose) {
      const carried = world.carriedPlaceable();
      const near = carried ? undefined : world.nearestPlaceable(pose, 2.2);
      const mine = world.self && world.self.name === world.homeOwnerName;
      const label = (kind: string) =>
        isPlaceableKind(kind) ? PLACEABLE_SPECS[kind].label : "piece";
      prompt.set(
        carried
          ? `E — set the ${label(carried.kind)} down`
          : mine && near
            ? `E — take up the ${label(world.placeables.get(near)?.kind ?? "")}`
            : null,
      );
      // The room announces itself once the claim's name arrives.
      if (world.homeLabel && world.homeLabel !== lastHomeLabel) {
        lastHomeLabel = world.homeLabel;
        chat.setAreaLabel(world.homeLabel);
      }
    } else if (areaPlan.flyerStack && pose) {
      const stack = areaPlan.flyerStack;
      const within = Math.hypot(pose.x - stack.x, pose.z - stack.z) <= FLYER_REACH_M;
      prompt.set(within ? "E — take a flyer" : null);
    } else {
      // A room with nothing to reach for (the Understreet) must clear the
      // last room's offer rather than wear it forever.
      prompt.set(null);
    }

    // While YOU are on air, your own stream playback stays off — hearing
    // yourself 10 seconds late is unbearable; check on a phone instead.
    if (areaPlan.mount) clubAudio.setLive(world.music.live && !broadcaster.isLive);
    else if (radio.tuned) clubAudio.setLive(true);
    camera.camera.getWorldDirection(listenerForward);
    clubAudio.updateListener(camera.camera.position, listenerForward, camera.camera.up);
    musicPill.update(world.music);
    booth.update(areaPlan.mount ?? "", world.music.live);

    renderer.info.reset();
    renderer.getDrawingBufferSize(drawingBuffer);
    post.setSize(drawingBuffer.x, drawingBuffer.y);
    post.render(area.scene, camera.camera);
    framesInArea++;

    chat.setStatus(world.status);
    if (world.self) chat.setSelfName(world.self.name);
  });

  void refreshNights();
  const nightsTimer = setInterval(() => void refreshNights(), NIGHTS_REFRESH_MS);
  window.addEventListener("beforeunload", () => clearInterval(nightsTimer));

  net.join(freecam?.room).catch((err) => {
    console.error("[bebop] join failed", err);
    world.status = "failed";
    chat.setStatus("failed");
  });

  if (import.meta.env.DEV) {
    // Debug handle for the console; stripped from production builds.
    (window as unknown as Record<string, unknown>).__bebop = {
      world,
      input,
      net,
      renderer,
      camera,
      clubAudio,
      broadcaster,
      get scene() {
        return area?.scene;
      },
      // The capture harness (tools/capture) polls this before it trusts a
      // screenshot: connected, an area on screen, streaming drained, and
      // enough frames rendered for lights and interpolation to settle.
      get captureReady() {
        return (
          world.status === "connected" &&
          area !== undefined &&
          (area.streamingPending?.() ?? 0) === 0 &&
          framesInArea >= 90
        );
      },
      // Smoothed fps — the wave-9 perf gate reads this per vista.
      get fps() {
        return gauge.stats().fps;
      },
      /**
       * The perf guard's readout: the frame-time DISTRIBUTION plus what
       * the frame cost to build. A mean hides the stalls that actually
       * ruin play, so the gate is p95, and `stalls` counts the frames
       * that blew past 20 ms outright.
       */
      // The post stack itself, for perf attribution from the console:
      // `__bebop.post.bypass = true` is the control group.
      post,
      /** Drop the frame-time history — call it after changing anything. */
      resetPerf() {
        gauge.reset();
        governor.reset();
      },
      get perf() {
        const stats = gauge.stats();
        const info = renderer.info;
        return {
          ...stats,
          tier: post.currentTier(),
          calls: info.render.calls,
          triangles: info.render.triangles,
          programs: info.programs?.length ?? 0,
          geometries: info.memory.geometries,
          textures: info.memory.textures,
        };
      },
      // Why captureReady is (not) true — for debugging stuck captures.
      get captureDebug() {
        return {
          status: world.status,
          hasArea: area !== undefined,
          streamingPending: area?.streamingPending?.() ?? 0,
          framesInArea,
          fps: gauge.stats().fps,
        };
      },
    };
  }
}
